// Phase 38 · 23b — created versus resolved.
//
// The chart itself was already right; what it never did was say what it shows.
// Two lines that never converge is the whole story of the open-ticket count,
// and a reader who has to derive that usually does not, so the foot band states
// it: how many days resolution fell behind intake, and where the worst day was.

import { AreaChart, LineChart, BarChart, AreaChartDataPoint, useThemeColors } from '../charts';
import { volumeFinding, volumeBucketLabel } from '../../features/dashboard/widgetFindings';
import { WidgetShell, WidgetEmpty, Found } from './WidgetShell';

interface TicketVolumeWidgetProps {
  data?: AreaChartDataPoint[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  chartType?: 'area' | 'line' | 'bar';
  title?: string;
}

function Legend({ created, resolved }: { created: string; resolved: string }) {
  return (
    <div className="flex items-center gap-[14px] pt-[3px]">
      {[
        { label: 'Created', color: created },
        { label: 'Resolved', color: resolved },
      ].map((s) => (
        <span key={s.label} className="inline-flex items-center gap-[6px]">
          <span className="h-[2px] w-[14px] flex-none" style={{ background: s.color }} aria-hidden />
          <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-ink-3)]">
            {s.label}
          </span>
        </span>
      ))}
    </div>
  );
}

export function TicketVolumeWidget({
  data,
  loading = false,
  error,
  onRetry,
  className = '',
  chartType = 'area',
  title,
}: TicketVolumeWidgetProps) {
  const { created, resolved } = useThemeColors({
    created: '--co-ink',
    resolved: '--co-green',
  });

  const points = data?.map((d) => ({
    name: String(d.name ?? ''),
    created: (d.created as number) || 0,
    resolved: (d.resolved as number) || 0,
  }));
  const found = volumeFinding(points);

  // Only bail out to the summary when there is genuinely NO activity in the
  // period. A single non-zero point (common for agents with little data) still
  // renders a chart — see `sparse` below.
  const active = points?.filter((p) => p.created > 0 || p.resolved > 0) ?? [];
  const hasActivity = active.length >= 1;
  const sparse = active.length <= 3;

  // Count in the unit the chart is bucketed by — a ten-point monthly series
  // used to be captioned "1 of 10 days. The busiest day, 2026-10".
  const unit = found?.unit ?? 'day';
  const units = (n: number) => `${n} ${unit}${n === 1 ? '' : 's'}`;

  const finding = found
    ? found.daysBehind === 0
      ? (
          <>
            <Found>Resolution kept pace every {unit}.</Found> {found.totalResolved} resolved against{' '}
            {found.totalCreated} created across {units(found.days)}.
          </>
        )
      : (
          <>
            <Found>
              Resolution fell behind intake on {found.daysBehind} of {units(found.days)}.
            </Found>{' '}
            The busiest {unit}, {volumeBucketLabel(found.peak.name)}, took {found.peak.created} in and closed{' '}
            {found.peak.resolved} — {found.totalCreated} created against {found.totalResolved} resolved
            overall.
          </>
        )
    : null;

  return (
    <WidgetShell
      title={title || 'Ticket Volume Trend'}
      subtitle={loading ? undefined : 'Created vs resolved over time'}
      action={loading ? undefined : <Legend created={created} resolved={resolved} />}
      loading={loading}
      error={error}
      onRetry={onRetry}
      className={className}
      finding={finding}
      bodyClassName="p-3"
      skeleton={
        <div className="h-full p-3">
          <div className="h-full animate-pulse rounded bg-[var(--co-sunken)]" />
        </div>
      }
      empty={
        !data || data.length === 0 ? (
          <WidgetEmpty title="No trend data" hint="The trend appears once tickets are created." />
        ) : !hasActivity ? (
          <WidgetEmpty
            title="No ticket activity in this period"
            hint={`${found?.totalCreated ?? 0} created, ${found?.totalResolved ?? 0} resolved.`}
          />
        ) : undefined
      }
    >
      {chartType === 'line' ? (
        <LineChart
          data={data!}
          lines={[
            { dataKey: 'created', name: 'Created', color: created, dot: sparse },
            { dataKey: 'resolved', name: 'Resolved', color: resolved, dot: sparse },
          ]}
          xAxisDataKey="name"
          showLegend={false}
        />
      ) : chartType === 'bar' ? (
        <BarChart
          data={data!}
          bars={[
            { dataKey: 'created', name: 'Created', color: created },
            { dataKey: 'resolved', name: 'Resolved', color: resolved },
          ]}
          xAxisDataKey="name"
          showLegend={false}
        />
      ) : (
        <AreaChart
          data={data!}
          areas={[
            { dataKey: 'created', name: 'Created', color: created, dot: sparse },
            { dataKey: 'resolved', name: 'Resolved', color: resolved, dot: sparse },
          ]}
          xAxisDataKey="name"
          showLegend={false}
        />
      )}
    </WidgetShell>
  );
}
